import { useState, useEffect } from "react";
import { Menu, Settings, Bell } from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL;

function Header({ pageTitle, onMenuClick, onSettingsClick, onBellClick }) {
  const [dateTime, setDateTime] = useState(new Date());
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 👇 poll unread inquiries count + listen for changes
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    const fetchUnread = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/sdaowner/inquiries/unread_count`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await res.json();
        if (res.ok && typeof data.unread === "number") {
          setUnreadCount(data.unread);
        }
      } catch (err) {
        console.error("Failed to fetch unread inquiry count:", err);
      }
    };

    fetchUnread(); // initial load

    const interval = setInterval(fetchUnread, 30000); // every 30s

    // 🔔 when Inquiry.jsx marks something as read, it dispatches this event
    const handleStatusChange = () => {
      fetchUnread();
    };
    window.addEventListener("inquiry-status-changed", handleStatusChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("inquiry-status-changed", handleStatusChange);
    };
  }, []);

  // Format the date as "12 Nov 2025, Wednesday"
  const formattedDate = dateTime.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const weekday = dateTime.toLocaleDateString("en-GB", { weekday: "long" });
  const formattedFullDate = `${formattedDate}, ${weekday}`;

  const formattedTime = dateTime.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 relative h-auto">
      {/* MOBILE (2 rows) */}
      <div className="sm:hidden px-4 py-3">
        {/* Row 1: menu + title + actions */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              className="p-2 rounded-md hover:bg-gray-100"
              onClick={onMenuClick}
              type="button"
              aria-label="Open menu">
              <Menu className="h-6 w-6 text-gray-700" />
            </button>

            <h1 className="text-gray-900 text-lg font-semibold truncate">
              {pageTitle}
            </h1>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              className="relative p-2 rounded-full bg-[#D2138C] hover:bg-[#D2138C] transition"
              onClick={onBellClick}
              aria-label="Notifications"
              type="button">
              <Bell className="h-5 w-5 text-white" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            <button
              className="p-2 rounded-full bg-[#D2138C] hover:bg-[#D2138C] transition"
              onClick={onSettingsClick}
              aria-label="Settings"
              type="button">
              <Settings className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Row 2: date/time (right side under the buttons) */}
        <div className="mt-2 flex items-center justify-end gap-3 text-[12px] font-medium text-gray-600">
          <span className="truncate max-w-[70%]">{formattedFullDate}</span>
          <span className="shrink-0">{formattedTime}</span>
        </div>
      </div>

      {/* DESKTOP (single row) */}
      <div className="hidden sm:flex h-16 px-6 items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-gray-900 text-2xl font-semibold">{pageTitle}</h1>
        </div>

        <div className="flex items-center gap-4 text-gray-700">
          <div className="flex items-center gap-4 text-sm font-medium">
            <span>{formattedFullDate}</span>
            <span>{formattedTime}</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              className="relative p-2 rounded-full bg-[#D2138C] hover:bg-[#D2138C] transition cursor-pointer"
              onClick={onBellClick}
              aria-label="Notifications"
              type="button">
              <Bell className="h-5 w-5 text-white" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            <button
              className="p-2 rounded-full bg-[#D2138C] hover:bg-[#D2138C] transition cursor-pointer"
              onClick={onSettingsClick}
              aria-label="Settings"
              type="button">
              <Settings className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
